import { inject, Injectable } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import { SCREENING_CONFIG_API_SERVICE_TOKEN } from '@nk/api-lib';
import { UpdateWatchlistHandlerService } from '@nk/company-block';
import {
  WatchlistAction,
  WatchlistChangeAction,
  WatchlistEntity,
  WatchlistMappingEntity,
  WithUuid,
} from '@nk/data-entities-lib';
import { MessageQueue, MessageQueueService } from '@nk/ui-lib';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { MonitoringConnectionService } from './monitoring-connection.service';
import { MonitoringOriginEntityService } from './monitoring-origin-entity.service';
import { ScreeningCaseService } from './screening-case.service';
import { ScreeningInputParamsService } from './screening-input-params.service';

@Injectable()
export class ScreeningService implements UpdateWatchlistHandlerService {
  private _watchlistChanges = new Subject<WatchlistChangeAction>();
  public watchlistChanges$ = this._watchlistChanges.asObservable();

  private _screeningConfigRefresh = new Subject<string>();
  public screeningConfigRefresh$ = this._screeningConfigRefresh.asObservable();

  private _isScreeningRefreshLoading = new BehaviorSubject<boolean>(false);
  public isScreeningRefreshLoading$: Observable<boolean> | undefined;
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private _monitoringOriginEntityService = inject(MonitoringOriginEntityService);
  private _monitoringConnectionService = inject(MonitoringConnectionService);
  private _screeningCaseService = inject(ScreeningCaseService);
  private _appService = inject(AppService);
  private _translationService = inject(TranslateService);
  private _messageQueueService = inject(MessageQueueService);

  constructor() {
    this.screeningConfigRefresh$
      .pipe(
        mergeMap((screeningConfigUuid: string) => {
          this._isScreeningRefreshLoading.next(true);
          const message = this.getMessageForScreeningSuggestions(screeningConfigUuid);
          this._messageQueueService.addMessage(message);
          return this._screeningConfigService.postJobScreeningAction(screeningConfigUuid).pipe(
            tap(() => {
              this._messageQueueService.removeMessage(message.key);
              this.invalidateScreeningCaches();
              this._screeningInputParamsService.triggerScreeningEntitiesRefresh();
              this._isScreeningRefreshLoading.next(false);
            }),
          );
        }),
      )
      .subscribe();

    this.watchlistChanges$
      .pipe(
        mergeMap((change: WatchlistChangeAction) => {
          return this._isWatchlistInScreeningConfig(change.payload.watchlistUuid).pipe(
            filter((isWatchlistInScreeningConfig) => isWatchlistInScreeningConfig),
            map(() => change),
          );
        }),
        switchMap((change) => this._reduceWatchlistChange(change)),
      )
      .subscribe(() => {
        this.invalidateScreeningCaches();
        this._screeningInputParamsService.triggerScreeningEntitiesRefresh();
      });
  }

  private _reduceWatchlistChange(action: WatchlistChangeAction): Observable<boolean> {
    switch (action.type) {
      case WatchlistAction.PostOneMapping:
        return this._updateWatchlistScreeningSuggestions(action.payload.watchlistUuid, action.payload.companyUuid);
      case WatchlistAction.PostMultipleMappings:
        return this._updateWatchlistScreeningSuggestions(action.payload.watchlistUuid);
      case WatchlistAction.DeleteOneMapping:
        return this._updateWatchlistScreeningSuggestions(action.payload.watchlistUuid, action.payload.companyUuid);
      case WatchlistAction.DeleteMultipleMappings:
        return this._updateWatchlistScreeningSuggestions(action.payload.watchlistUuid);
      case WatchlistAction.DeleteWatchlist:
        //  TODO: Check this
        return this._updateWatchlistScreeningSuggestions(action.payload.watchlistUuid);
      default:
        console.warn('Unexpected action', action);
        return of(false);
    }
  }

  public registerWatchlistChange(change: WatchlistChangeAction): void {
    this._watchlistChanges.next(change);
  }

  public registerScreeningConfigRefresh(screeningConfigUuid: string): void {
    this._screeningConfigRefresh.next(screeningConfigUuid);
  }

  public invalidateScreeningCaches(): void {
    this._monitoringOriginEntityService.monitoringOriginEntityCompositeCollectionCache.invalidateAllCache();
    this._screeningCaseService.screeningEntityScreeningCaseCompositeCollectionCache.invalidateAllCache();
    this._monitoringConnectionService.monitoringConnectionCompositeCollectionCache.invalidateAllCache();
    this._screeningConfigService.screeningActionCompositeCollectionCache.invalidateAllCache();
    this._screeningConfigService.screeningConfigStatsAggregateCache.invalidateAllCache();
    this._screeningConfigService.screeningAuditLogReportMetaCompositeListCache.invalidateAllCache();
    this._screeningConfigService.screeningConfigWatchlistListCache.invalidateAllCache();
  }

  private _getCurrentScreeningConfigUuid(): Observable<string> {
    const appCustomerUuid$ = this._appService.identityDataCache
      .get()
      .pipe(map((identity) => identity.app_customer.uuid));
    return appCustomerUuid$.pipe(
      switchMap((appCustomerUuid) => this._screeningConfigService.screeningConfigsListCache.get(appCustomerUuid)),
      map((data) => data && data[0]?.uuid),
    );
  }
  public removeWatchlistMappingWarning(watchlistMapping: WatchlistMappingEntity): Observable<string | null> {
    return this._getCurrentScreeningConfigUuid().pipe(
      switchMap((screeningConfigUuid) => {
        return this._screeningConfigService.screeningConfigWatchlistListCache.get(
          watchlistMapping.company_uuid,
          screeningConfigUuid,
          watchlistMapping.watchlist_uuid,
        );
      }),
      map((screeningConfig) => {
        if (screeningConfig && screeningConfig.length) {
          return this._translationService.instant(
            'watchlist_update_warning.remove.watchlist_part_of_screening',
          ) as string;
        } else {
          return null;
        }
      }),
    );
  }

  private _isWatchlistInScreeningConfig(watchlistUuid: string): Observable<boolean> {
    return this._getCurrentScreeningConfigUuid().pipe(
      switchMap((screeningConfigUuid) => {
        if (screeningConfigUuid) {
          return this._screeningConfigService.screeningConfigWatchlistListCache.get(
            undefined,
            screeningConfigUuid,
            watchlistUuid,
          );
        }
        return of([]);
      }),
      map((screeningConfig) => {
        return !!(screeningConfig && screeningConfig.length);
      }),
    );
  }

  public addToWatchlistWarning(watchlistUuid: string): Observable<string | null> {
    return this._isWatchlistInScreeningConfig(watchlistUuid).pipe(
      map((isWatchlistInScreeningConfig) => {
        if (isWatchlistInScreeningConfig) {
          return this._translationService.instant('watchlist_update_warning.add.watchlist_part_of_screening') as string;
        } else {
          return null;
        }
      }),
    );
  }

  // Not providing company will update the whole screening config..
  private _updateWatchlistScreeningSuggestions(
    watchlistUuid: string,
    companyUuid: string | null = null,
  ): Observable<boolean> {
    return this._getCurrentScreeningConfigUuid().pipe(
      switchMap((screeningConfigUuid) => {
        return this._screeningConfigService.screeningConfigWatchlistListCache.get(
          undefined,
          screeningConfigUuid,
          watchlistUuid,
        );
      }),
      switchMap((screeningConfig) => {
        if (!screeningConfig || screeningConfig.length == 0) {
          throw new Error('Tried to do screening sync, but no screening config could be found');
        }
        const screeningConfigUuid = screeningConfig[0].screening_config_uuid;
        const message = this.getMessageForScreeningSuggestions(screeningConfigUuid);
        this._messageQueueService.addMessage(message);

        return this._screeningConfigService.postJobScreeningAction(screeningConfigUuid, companyUuid).pipe(
          tap(() => {
            this._messageQueueService.removeMessage(message.key);
            this.invalidateScreeningCaches();
            this._screeningInputParamsService.triggerScreeningEntitiesRefresh();
          }),
          map(() => true),
        );
      }),
    );
  }

  public getMessageForScreeningSuggestions(screeningConfigUuid: string): MessageQueue {
    const key = `setup_screening-${screeningConfigUuid}`;
    const message = this._translationService.instant(
      extractTranslation('bottom_message.entity_monitoring_suggestions_progress') as string,
    ) as string;
    return {
      message,
      key,
    };
  }

  public removeWatchlistWarning(watchlist: WithUuid<WatchlistEntity>): Observable<string | null> {
    return this._isWatchlistInScreeningConfig(watchlist.uuid).pipe(
      map((isWatchlistInScreeningConfig) => {
        if (isWatchlistInScreeningConfig) {
          return this._translationService.instant(
            'watchlist_update_warning.delete_whole_watchlist.watchlist_part_of_screening',
          ) as string;
        } else {
          return null;
        }
      }),
    );
  }
}
